import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
} from '@ouropix/ui';
import { Key, Loader2, LogOut, Shield } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { verify2FA } from '@/lib/auth';
import type { AdminSession } from '@/stores/auth';

interface RouteSearch {
  userId?: string;
  tempToken?: string;
}

export const Route = createFileRoute('/verify-2fa')({
  validateSearch: (search: Record<string, unknown>): RouteSearch => ({
    userId: typeof search.userId === 'string' ? search.userId : undefined,
    tempToken: typeof search.tempToken === 'string' ? search.tempToken : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (!search.userId || !search.tempToken) {
      throw redirect({ to: '/login' });
    }
  },
  component: AdminVerify2FAPage,
});

function AdminVerify2FAPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const setSession = useAuthStore((s) => s.setSession);
  const logout = useAuthStore((s) => s.logout);

  const userId = search.userId!;
  const tempToken = search.tempToken!;

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);

  async function handleVerify() {
    const trimmed = code.trim();

    if (!useBackupCode && (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed))) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }
    if (useBackupCode && (trimmed.length !== 8 || !/^[A-Fa-f0-9]{8}$/.test(trimmed))) {
      toast.error('Digite um código de backup válido (8 caracteres)');
      return;
    }

    setLoading(true);
    try {
      const result = await verify2FA({ userId, code: trimmed, tempToken, rememberDevice: rememberDevice && !useBackupCode });

      if (!result.success) {
        toast.error(result.message || 'Código inválido. Verifique e tente novamente.');
        setCode('');
        return;
      }

      if (result.usedBackupCode) {
        toast.warning('Código de backup utilizado. Considere gerar novos códigos.');
      }

      const session: AdminSession = {
        userId,
        email: result.email || '',
        fullName: result.fullName || '',
        role: result.role || 'moderator',
        twoFactorEnabled: true,
        ...(result.token
          ? {
              token: result.token,
              refreshToken: result.refreshToken || '',
              tokenExpiresAt: Date.now() + (result.expiresIn ?? 7 * 24 * 3600) * 1000,
            }
          : {}),
      };
      setSession(session);
      toast.success('Verificação concluída!');
      navigate({ to: '/dashboard', replace: true });
    } catch {
      toast.error('Código inválido. Verifique e tente novamente.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !loading) {
      handleVerify();
    }
  }

  function handleLogout() {
    logout();
    navigate({ to: '/login', replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <div className="flex items-center justify-between p-4 md:p-6">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <span className="font-semibold text-sm">Verificação de Segurança</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="gap-2 text-muted-foreground"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </Button>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-7 w-7 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl">Verificação de Dois Fatores</CardTitle>
            <CardDescription>
              Digite o código do seu aplicativo autenticador para acessar o painel
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {!useBackupCode ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="totp-code">Código de verificação</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={handleKeyDown}
                    className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Abra seu app autenticador (Google Authenticator, Authy, etc.)
                  </p>
                </div>

                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setUseBackupCode(true);
                    setCode('');
                  }}
                  className="w-full text-muted-foreground"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Usar código de backup
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="backup-code">Código de backup</Label>
                  <Input
                    id="backup-code"
                    type="text"
                    maxLength={8}
                    placeholder="ABCD1234"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
                    onKeyDown={handleKeyDown}
                    className="text-center text-xl font-mono tracking-widest h-14"
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Digite um dos seus códigos de backup salvos
                  </p>
                </div>

                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setUseBackupCode(false);
                    setCode('');
                  }}
                  className="w-full text-muted-foreground"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Usar código do app autenticador
                </Button>
              </>
            )}

            {!useBackupCode && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-device"
                  checked={rememberDevice}
                  onCheckedChange={(v) => setRememberDevice(v === true)}
                />
                <Label htmlFor="remember-device" className="text-sm font-normal cursor-pointer">
                  Lembrar este dispositivo por 30 dias
                </Label>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex-1"
                disabled={loading}
              >
                Sair
              </Button>
              <Button
                onClick={handleVerify}
                disabled={loading || (useBackupCode ? code.length !== 8 : code.length !== 6)}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Verificar'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
